import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProtectedRoute from './ProtectedRoute';
import RouteErrorBoundary from './RouteErrorBoundary';
import { lazyWithRetry } from './lazyWithRetry';
import { shouldNoIndexRoute } from './routeIndexing';
import PublicLayout from '../pages/layout/PublicLayout';
import { homePathForRole, isReservedUsername } from '../services/userProfile';
import LandingPage from '../pages/events/LandingPage';

const EventDetailPage = lazyWithRetry(() => import('../pages/events/EventDetailPage'));
const EventHubPage = lazyWithRetry(() => import('../pages/events/EventHubPage'));
const WeeklyEventsPage = lazyWithRetry(() => import('../pages/events/WeeklyEventsPage'));
const HalloweenEvents = lazyWithRetry(() => import('../pages/events/halloween'));
const EventPublicEditPage = lazyWithRetry(() => import('../pages/events/EventPublicEditPage'));
const PlacesPage = lazyWithRetry(() => import('../pages/places/PlacesPage'));
const PlaceDetailPage = lazyWithRetry(() => import('../pages/places/PlaceDetailPage'));
const ArtistsPage = lazyWithRetry(() => import('../pages/artists/ArtistsPage'));
const ArtistDetailPage = lazyWithRetry(() => import('../pages/artists/ArtistDetailPage'));
const ProfilePage = lazyWithRetry(() => import('../pages/profile/ProfilePage'));
const ProfileArtistPage = lazyWithRetry(() => import('../pages/profile/ProfileArtistPage'));
const ShopPage = lazyWithRetry(() => import('../pages/shop/ShopPage'));
const FriendsPage = lazyWithRetry(() => import('../pages/friends/FriendsPage'));
const ChatShell = lazyWithRetry(() => import('../pages/friends/chat/ChatShell'));
const ChatEmpty = lazyWithRetry(() =>
  import('../pages/friends/chat/ChatShell').then((m) => ({ default: m.ChatEmpty }))
);
const ChatThread = lazyWithRetry(() => import('../pages/friends/chat/ChatThread'));
// Parked Bucket Lists — restore list/detail lazy imports to bring the page back.
// const SidequestsListPage = lazyWithRetry(
//   () => import('../pages/wip/sidequests/SidequestsListPage')
// );
// const SidequestDetailPage = lazyWithRetry(
//   () => import('../pages/wip/sidequests/SidequestDetailPage')
// );
const LegacySidequestRedirect = lazyWithRetry(
  () => import('../pages/wip/sidequests/LegacySidequestRedirect')
);
const GameFogPage = lazyWithRetry(() => import('../pages/game/GameFogPage'));
const GameCollectionPage = lazyWithRetry(() => import('../pages/game/GameCollectionPage'));
const GameMinePage = lazyWithRetry(() => import('../pages/game/GameMinePage'));
const MemoryPage = lazyWithRetry(() => import('../pages/game/MemoryPage'));
const Signup = lazyWithRetry(() => import('../pages/login/Signup'));
const PartnerPortal = lazyWithRetry(() => import('../pages/partner-portal/PartnerPortal'));
const GameMakerPage = lazyWithRetry(() => import('../pages/game-maker/GameMakerPage'));
const AdminHub = lazyWithRetry(() => import('../pages/admin-hub/AdminHub'));
const SharedCalendarPage = lazyWithRetry(() => import('../pages/friends/SharedCalendarPage'));
const PublicUserPage = lazyWithRetry(() => import('../pages/friends/PublicUserPage'));
const BlogAstronomskiPage = lazyWithRetry(
  () => import('../pages/blog/BlogAstronomskiPage')
);
const BlogMjesecPage = lazyWithRetry(() => import('../pages/blog/BlogMjesecPage'));
const BlogWoodyPage = lazyWithRetry(() => import('../pages/blog/BlogWoodyPage'));
const BlogIndexPage = lazyWithRetry(() => import('../pages/blog/BlogIndexPage'));
const VibecheckUploadPage = lazyWithRetry(
  () => import('../pages/vibecheck/VibecheckUploadPage')
);
const ExploreBosnaPage = lazyWithRetry(
  () => import('../pages/explore-bosna/ExploreBosnaPage')
);
const LoyaltyVenuePage = lazyWithRetry(() => import('../pages/loyalty/LoyaltyVenuePage'));
const Sova3Page = lazyWithRetry(() => import('../pages/sova3/Sova3Page'));
const Sova3AdminPage = lazyWithRetry(() => import('../pages/sova3/admin/Sova3AdminPage'));
const Fabrika2Page = lazyWithRetry(() => import('../pages/fabrika2/Fabrika2Page'));
const GiveawayPage = lazyWithRetry(() => import('../pages/events/giveaway/GiveawayPage'));
const GiveawayDemoPage = lazyWithRetry(() => import('../pages/events/giveaway/GiveawayDemoPage'));

function RouteFallback({ label = 'Loading...' }) {
  return (
    <div className="pub-loading" role="status">
      {label}
    </div>
  );
}

/** Preserve old bookmarks: /events/halloween → /halloween, including ?view=list. */
function HalloweenLegacyRedirect() {
  const location = useLocation();
  return <Navigate to={`/halloween${location.search}${location.hash}`} replace />;
}

/** Preserve old bookmarks: /admin/... → /adminhub/... */
function AdminLegacyRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/admin\/?/, '');
  const to = `/adminhub${rest ? `/${rest}` : ''}${location.search}${location.hash}`;
  return <Navigate to={to} replace />;
}

function setCrawlerDirective(content) {
  ['robots', 'googlebot'].forEach((name) => {
    let meta = document.querySelector(`meta[name="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  });
}

/** Preserve old invite links: /u/:username → /:username */
function LegacyUsernameRedirect() {
  const { username } = useParams();
  const key = String(username || '').trim();
  return <Navigate to={key ? `/${encodeURIComponent(key)}` : '/'} replace />;
}

/**
 * Defense if a reserved app path ever hits /:username (e.g. ranking edge cases).
 * Sends known shells to their real routes instead of an empty “no profile” page.
 */
function PublicUsernameOrReserved() {
  const { username } = useParams();
  if (isReservedUsername(username)) {
    const key = String(username || '').trim().toLowerCase();
    if (key === 'friends') return <Navigate to="/friends" replace />;
    if (key === 'profile') return <Navigate to="/profile" replace />;
    if (key === 'shop') return <Navigate to="/shop" replace />;
    if (key === 'levels') return <Navigate to="/profile" replace />;
    if (key === 'partner') return <Navigate to="/partner" replace />;
    if (key === 'adminhub' || key === 'admin') return <Navigate to="/adminhub" replace />;
    if (key === 'game-maker') return <Navigate to="/game-maker" replace />;
    if (key === 'login') return <Navigate to="/login" replace />;
    if (key === 'signup') return <Navigate to="/signup" replace />;
    if (key === 'game' || key === 'explore') return <Navigate to="/game" replace />;
    if (key === 'explore-bosna' || key === 'explorebosna') return <Navigate to="/explore-bosna" replace />;
    if (key === 'loyalty' || key === 'sova2') return <Navigate to="/sova2" replace />;
    if (key === 'sova3') return <Navigate to="/sova3" replace />;
    if (key === 'sova3-admin') return <Navigate to="/sova3-admin" replace />;
    if (key === 'fabrika2') return <Navigate to="/fabrika2" replace />;
    if (key === 'sidequests') return <Navigate to="/sidequests" replace />;
    if (key === 'places') return <Navigate to="/places" replace />;
    if (key === 'artists') return <Navigate to="/artists" replace />;
    if (key === 'events') return <Navigate to="/events" replace />;
    if (key === 'desavanja') return <Navigate to="/desavanja" replace />;
    if (key === 'blog') return <Navigate to="/blog" replace />;
    if (key === 'vibecheck' || key === 'upload-vibecheck') {
      return <Navigate to="/vibecheck" replace />;
    }
    return <Navigate to="/" replace />;
  }
  return <PublicUserPage />;
}

function AuthLandingRoute({ children }) {
  const { currentUser, role, userProfile, authLoading, profileReady } = useAuth();

  const waitingForProfile = Boolean(currentUser) && !profileReady;
  if (authLoading || waitingForProfile) {
    return <RouteFallback label="Loading..." />;
  }

  if (currentUser && !userProfile?.disabled) {
    return <Navigate to={homePathForRole(role)} replace />;
  }

  return children;
}

/** Bookmarks / Selenium: /login and /signup open the auth modal on /. */
function AuthDeepLink({ mode }) {
  const auth = mode === 'signup' ? 'signup' : 'signin';
  return <Navigate to={`/?auth=${auth}`} replace />;
}

function PlacesOrDetailPage() {
  const location = useLocation();
  const { placeId } = useParams();
  // Hard navigation / crawl: standalone detail. List + modal stay on PlacesPage.
  if (placeId && location.state?.modal !== true) {
    return <PlaceDetailPage />;
  }
  return <PlacesPage />;
}

const AppRouter = () => {
  const location = useLocation();

  useEffect(() => {
    setCrawlerDirective(
      shouldNoIndexRoute(location.pathname) ? 'noindex, nofollow' : 'index, follow'
    );
  }, [location.pathname]);

  return (
    <RouteErrorBoundary label="this page">
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Role hubs + auth first so they never compete with /:username */}
          <Route
            path="/login"
            element={(
              <AuthLandingRoute>
                <AuthDeepLink mode="signin" />
              </AuthLandingRoute>
            )}
          />
          <Route
            path="/signup"
            element={(
              <AuthLandingRoute>
                <AuthDeepLink mode="signup" />
              </AuthLandingRoute>
            )}
          />
          <Route
            path="/signup/partner"
            element={(
              <AuthLandingRoute>
                <Signup />
              </AuthLandingRoute>
            )}
          />

          <Route
            path="/partner/*"
            element={(
              <ProtectedRoute requiredRole="partner">
                <PartnerPortal />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/game-maker/*"
            element={(
              <ProtectedRoute requiredRole="gameMaker">
                <GameMakerPage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/adminhub/*"
            element={(
              <ProtectedRoute requiredRole="admin">
                <AdminHub />
              </ProtectedRoute>
            )}
          />
          <Route path="/admin/*" element={<AdminLegacyRedirect />} />
          <Route path="/admin" element={<Navigate to="/adminhub" replace />} />
          <Route path="/sova3-admin" element={<Sova3AdminPage />} />

          <Route path="/me" element={<Navigate to="/profile#calendar" replace />} />

          <Route element={<PublicLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/events/edit/:eventId/:token" element={<EventPublicEditPage />} />
            <Route path="/giveaway" element={<GiveawayPage />} />
            <Route path="/giveaway-demo" element={<GiveawayDemoPage />} />
            <Route path="/halloween" element={<HalloweenEvents />} />
            <Route path="/events/halloween" element={<HalloweenLegacyRedirect />} />
            <Route path="/events/concerts" element={<EventHubPage hubId="concerts" />} />
            <Route path="/events/this-weekend" element={<EventHubPage hubId="weekend" />} />
            <Route path="/events/week" element={<WeeklyEventsPage lang="en" />} />
            <Route path="/events" element={<EventHubPage hubId="events" />} />
            <Route path="/desavanja/sedmica" element={<WeeklyEventsPage lang="bs" />} />
            <Route path="/desavanja" element={<EventHubPage hubId="desavanja" />} />
            <Route path="/sarajevo-events" element={<Navigate to="/" replace />} />
            <Route path="/desavanja-sarajevo" element={<Navigate to="/" replace />} />
            <Route path="/events/:eventId" element={<EventDetailPage />} />
            <Route path="/places/:placeId?" element={<PlacesOrDetailPage />} />
            <Route path="/artists/:slug" element={<ArtistDetailPage />} />
            <Route path="/artists" element={<ArtistsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/artist" element={<ProfileArtistPage />} />
            <Route path="/profile/mine" element={<GameMinePage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/sova2" element={<LoyaltyVenuePage />} />
            <Route path="/sova3/q/:spotId" element={<Sova3Page />} />
            <Route path="/sova3" element={<Sova3Page />} />
            <Route path="/fabrika2/q/:spotId" element={<Fabrika2Page />} />
            <Route path="/fabrika2" element={<Fabrika2Page />} />
            <Route path="/loyalty" element={<Navigate to="/sova2" replace />} />
            <Route path="/loyalty/:venueSlug" element={<Navigate to="/sova2" replace />} />
            <Route path="/levels" element={<Navigate to="/profile" replace />} />
            <Route path="/friends" element={<FriendsPage />} />
            <Route path="/friends/chat" element={<ChatShell />}>
              <Route index element={<ChatEmpty />} />
              <Route path=":chatId" element={<ChatThread />} />
            </Route>
            <Route path="/c/:token" element={<SharedCalendarPage />} />
            <Route path="/sidequests" element={<Navigate to="/profile" replace />} />
            <Route path="/sidequests/:slug" element={<Navigate to="/profile" replace />} />
            <Route path="/game/bucket-lists" element={<Navigate to="/profile" replace />} />
            <Route path="/game/collection" element={<GameCollectionPage />} />
            <Route path="/game/mine" element={<Navigate to="/profile/mine" replace />} />
            <Route path="/game" element={<GameFogPage />} />
            <Route path="/game/:slug" element={<LegacySidequestRedirect />} />
            <Route path="/explore/collection" element={<Navigate to="/game/collection" replace />} />
            <Route path="/explore/mine" element={<Navigate to="/profile/mine" replace />} />
            <Route path="/explore" element={<Navigate to="/game" replace />} />
            <Route path="/explore-bosna" element={<ExploreBosnaPage />} />
            <Route path="/memory/:id" element={<MemoryPage />} />
            <Route path="/vibecheck" element={<VibecheckUploadPage />} />
            <Route path="/upload-vibecheck" element={<Navigate to="/vibecheck" replace />} />
            <Route
              path="/blog/pomracenje-perzeidi-planete"
              element={<BlogAstronomskiPage />}
            />
            <Route
              path="/blog/pomracenje-mjeseca"
              element={<BlogMjesecPage />}
            />
            <Route
              path="/blog/woody-in-sarajevo"
              element={<BlogWoodyPage />}
            />
            <Route
              path="/blog/astronomski"
              element={<Navigate to="/blog/pomracenje-perzeidi-planete" replace />}
            />
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/blog/:slug" element={<Navigate to="/" replace />} />
            <Route path="/u/:username" element={<LegacyUsernameRedirect />} />
            <Route path="/:username" element={<PublicUsernameOrReserved />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
};

export default AppRouter;
